import Phaser from 'phaser';
import { sfx } from '../audio/sfx';
import { BUTTON, RIBBON_H, SLICE, TEX, ribbonFaceY } from './assets';
import { bindButtonPress } from './buttonPress';
import { coverHitArea } from './hitArea';
import { displayStyle, inkStyle, textResolution } from './theme';

export type ButtonSkin = 'gold' | 'berry';

export interface PaintedButton {
  slice: Phaser.GameObjects.NineSlice;
  label: Phaser.GameObjects.Text;
  setSkin: (skin: ButtonSkin) => void;
  setLabel: (text: string) => void;
  isDown: () => boolean;
}

type HitTarget = Phaser.GameObjects.GameObject & {
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
};

/** Match the pointer target to the drawn size, plus thumb padding and an 88px minimum. */
export function fitInteractive<T extends HitTarget>(target: T, drawnW: number, drawnH: number): T {
  const area = coverHitArea(drawnW, drawnH, target.width, target.height, target.scaleX, target.scaleY);
  const rect = new Phaser.Geom.Rectangle(area.x, area.y, area.w, area.h);
  if (!target.input) target.setInteractive(rect, Phaser.Geom.Rectangle.Contains);
  const input = target.input;
  if (input) {
    // disableInteractive only clears this flag. The object stays in the input
    // list, so a hidden results button stays dead after Collect unless a fit turns it back on.
    input.enabled = true;
    input.hitArea = rect;
    input.customHitArea = true;
    input.cursor = 'pointer';
  }
  return target;
}

export function paintButtonSlice(
  slice: Phaser.GameObjects.NineSlice,
  skin: ButtonSkin,
  pressed: boolean,
  width: number,
): void {
  const spec = BUTTON[skin];
  slice.setTexture(pressed ? spec.pressed : spec.normal);
  slice.setSlices(width, spec.h, SLICE.button.left, SLICE.button.right, 0, 0);
}

export function addPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  h: number,
  skin: 'wood' | 'parchment',
  depth = 5,
): Phaser.GameObjects.NineSlice {
  const spec = skin === 'wood' ? { key: TEX.panelWood, ...SLICE.wood } : { key: TEX.panelParchment, ...SLICE.parchment };
  return scene.add
    .nineslice(x + w / 2, y + h / 2, spec.key, undefined, w, h, spec.left, spec.right, spec.top, spec.bottom)
    .setDepth(depth);
}

export function addRibbon(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  depth = 20,
): Phaser.GameObjects.NineSlice {
  return scene.add
    .nineslice(x, y, TEX.ribbon, undefined, w, RIBBON_H, SLICE.ribbon.left, SLICE.ribbon.right, 0, 0)
    .setDepth(depth);
}

export function addButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  w: number,
  label: string,
  onClick: () => void,
  opts?: { skin?: ButtonSkin; fontSize?: string; depth?: number },
): PaintedButton {
  const depth = opts?.depth ?? 12;
  const skin = opts?.skin ?? 'gold';
  const slice = scene.add
    .nineslice(x, y, BUTTON[skin].normal, undefined, w, BUTTON[skin].h, SLICE.button.left, SLICE.button.right, 0, 0)
    .setDepth(depth);
  const text = addText(scene, x, y - 1, label, displayStyle(opts?.fontSize ?? '32px'))
    .setOrigin(0.5)
    .setDepth(depth + 1);
  const state = { skin, down: false };
  const paint = () => {
    paintButtonSlice(slice, state.skin, state.down, w);
    fitInteractive(slice, w, BUTTON[state.skin].h);
    text.setY(Math.round(state.down ? y + 2 : y - 1));
  };
  bindButtonPress(scene.input, slice, {
    onPress() {
      state.down = true;
      paint();
    },
    onRelease(activate) {
      state.down = false;
      paint();
      if (!activate) return;
      try {
        sfx.unlock();
        sfx.play('ui');
      } catch {
        // A failed cue must not swallow the click.
      }
      onClick();
    },
  });
  paint();
  return {
    slice,
    label: text,
    setSkin(next) {
      state.skin = next;
      paint();
    },
    setLabel(next) {
      text.setText(next);
    },
    isDown: () => state.down,
  };
}

/** Place text on whole pixels and rasterize it at the device pixel ratio. */
export function addText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  content: string,
  style: Phaser.Types.GameObjects.Text.TextStyle,
): Phaser.GameObjects.Text {
  const text = scene.add.text(Math.round(x), Math.round(y), content, style);
  text.setResolution(textResolution());
  return text;
}

/** Ribbon-style heading, dark on the cream banner, centered on the flat face. */
export function ribbonLabel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  size = '28px',
  depth = 21,
): Phaser.GameObjects.Text {
  return addText(scene, x, ribbonFaceY(y), text, inkStyle(size)).setOrigin(0.5).setDepth(depth);
}

export function addSoundButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  depth = 30,
  onChange?: () => void,
): Phaser.GameObjects.Image {
  const size = 72;
  const displayH = size * (112 / 107);
  const icon = scene.add
    .image(x, y, sfx.muted ? TEX.soundOff : TEX.soundOn)
    .setDisplaySize(size, displayH)
    .setDepth(depth);
  const fit = () => fitInteractive(icon, size, displayH);
  fit();
  icon.on('pointerdown', () => {
    icon.setScale(icon.scaleX * 0.94, icon.scaleY * 0.94);
    fit();
  });
  icon.on('pointerup', () => {
    try {
      sfx.unlock();
      sfx.toggle();
      sfx.play('ui');
    } catch {
      // A failed cue must not swallow the click.
    }
    icon.setTexture(sfx.muted ? TEX.soundOff : TEX.soundOn);
    icon.setDisplaySize(size, displayH);
    fit();
    onChange?.();
  });
  icon.on('pointerout', () => {
    icon.setDisplaySize(size, displayH);
    fit();
  });
  return icon;
}
